import type { Theme, WithStyles } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import type { WithClientOptions } from '@paintscout/ui'
import { withClientOptions, FormSectionTitle, InputField, OverridableInputField } from '@paintscout/ui'
import type { UpdateableArea } from '@paintscout/util/builder'
import { getFeatures, getObjectLabels } from '@paintscout/util/builder'
import get from 'lodash/get'
import React from 'react'

export interface AreaTotalsFormProps extends WithStyles<typeof styles>, WithClientOptions {
  area?: UpdateableArea
  short?: boolean
  hideLabel?: boolean
  onChange?: (event: any, area: UpdateableArea) => void
  onComplete?: (event: any) => void
}

export const styles = (theme: Theme) => {
  const { pxToRem } = theme.typography

  return createStyles({
    root: {},
    labelSection: {
      paddingTop: 0
    },
    descriptionSection: {
      paddingTop: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    substrateStringWrapper: {
      display: 'block'
    },
    substrateString: {
      ...theme.typography.body1
    },
    editButtonWrapper: {
      ...theme.typography.button,
      marginLeft: pxToRem(10)
    },
    editButton: {
      color: theme.palette.primary.main,
      cursor: 'pointer'
    },

    radioGroup: {
      marginTop: theme.spacing()
    },

    radioButton: {
      height: '22px'
    },

    calculatedDimensionsSection: {
      display: 'grid',
      gridGap: theme.spacing(2),
      gridTemplateColumns: '0.5fr 0.5fr'
    },
    fadeCalculatedDimensionsSection: {
      opacity: 0.5
    },
    dimensionsSection: {
      display: 'grid',
      gridTemplateColumns: '0.333fr 0.333fr 0.333fr',
      gridGap: theme.spacing(2)
    },
    dimensionsSectionSurface: {
      gridTemplateColumns: '0.5fr 0.5fr'
    },
    dimensionInput: {},
    notesSection: {},
    notesButtons: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-around'
    },
    noteInput: {
      marginBottom: theme.spacing(2)
    },
    formTitle: {
      paddingBottom: 0
    },
    formTitleContainer: {
      paddingBottom: 0,
      marginBottom: -theme.spacing(4),
      marginTop: theme.spacing()
    },
    label: {
      ...theme.typography.overline,
      border: 'none'
    }
  })
}

function AreaTotalsForm(props: AreaTotalsFormProps) {
  const { classes, clientOptions, area } = props
  const { options } = clientOptions
  const objectLabels = getObjectLabels({ options })
  const features = getFeatures({ options })
  const hasMaterialCost = get(features, 'quotes.materialCost.enabled', false)

  const { totals } = area

  const prepValue = totals.prep.useCustom ? totals.prep.custom : totals.prep.default
  const hoursValue = totals.hours.useCustom ? totals.hours.custom : totals.hours.default

  const materialsValue = totals.materials.useCustom ? totals.materials.custom : totals.materials.default
  const priceValue = totals.price.useCustom ? totals.price.custom : totals.price.default

  return (
    <div className={classes.root}>
      <Grid container={true} spacing={3} justifyContent="flex-start" alignItems="flex-start">
        <Grid item={true} xs={12} sm={12} md={12} classes={{ root: classes.formTitleContainer }}>
          <FormSectionTitle title={'Hours'} variant={'h4'} classes={{ root: classes.formTitle }} />
        </Grid>
        <Grid item={true} xs={12} sm={12} md={4}>
          <OverridableInputField
            format={'hours'}
            label={objectLabels.prep.value}
            resetBlank={true}
            value={totals.prep}
            hideHelp={true}
            onChange={handleTotalsChange('prep')}
            fullWidth={true}
            autoSelect={true}
          />
        </Grid>
        <Grid item={true} xs={12} sm={12} md={4}>
          <OverridableInputField
            format={'hours'}
            label={objectLabels.labour.value}
            resetBlank={true}
            value={totals.hours}
            onChange={handleTotalsChange('hours')}
            fullWidth={true}
            autoSelect={true}
          />
        </Grid>
        <Grid item={true} xs={12} sm={12} md={4}>
          <InputField
            format={'hours'}
            label={'Total Hours'}
            value={prepValue + hoursValue}
            disabled={true}
            fullWidth={true}
          />
        </Grid>
      </Grid>
      <Grid container={true} spacing={3} justifyContent="flex-start" alignItems="flex-start">
        <Grid item={true} xs={12} sm={12} md={12} classes={{ root: classes.formTitleContainer }}>
          <FormSectionTitle title={'Price'} variant={'h4'} classes={{ root: classes.formTitle }} />
        </Grid>
        {hasMaterialCost && (
          <Grid item={true} xs={12} sm={12} md={4}>
            <OverridableInputField
              format={'price'}
              label={'Materials'}
              resetBlank={true}
              value={totals.materials}
              onChange={handleTotalsChange('materials')}
              fullWidth={true}
              autoSelect={true}
            />
          </Grid>
        )}
        <Grid item={true} xs={12} sm={12} md={4}>
          <OverridableInputField
            format={'price'}
            resetBlank={true}
            label={objectLabels.labour.value}
            value={totals.price}
            onChange={handleTotalsChange('price')}
            fullWidth={true}
            autoSelect={true}
          />
        </Grid>
        {hasMaterialCost && (
          <Grid item={true} xs={12} sm={12} md={4}>
            <InputField
              format={'price'}
              label={'Total Price'}
              value={priceValue + materialsValue}
              fullWidth={true}
              disabled={true}
            />
          </Grid>
        )}
      </Grid>
    </div>
  )

  function handleTotalsChange(name: string) {
    return function (value: { useCustom: boolean; custom: number | string; default: number | string }) {
      const newArea = {
        ...area,
        totals: {
          ...area.totals,
          [name]: { ...value }
        }
      }

      if (props.onChange) {
        props.onChange(event, newArea)
      }
    }
  }
}

export default withStyles(styles)(withClientOptions(AreaTotalsForm))
